import { Menu, Popover, Transition } from "@headlessui/react"
import moment from "moment"
import { useRouter } from "next/router"
import { type FC, Fragment, useContext, useEffect, useRef, useState } from "react"

import {
  type BaseProductFragment,
  type DeliveryMethod,
  ProductType,
  UserActivityEventType,
} from "../generated/graphql"
import { AppContext } from "../providers/AppProvider"
import { calculateSubtotalPrice, productInCartCount } from "../utils/cart"
import { formatVariantDisplay, warmupStripeJs } from "../utils/checkout"
import { checkoutItems } from "../utils/checkoutActivity"
import { DATE_FORMAT_DAY_MONTH, DATE_FORMAT_DAY_MONTH_TIME } from "../utils/dates"
import { divideNumberByGrade } from "../utils/divideNumberByGrade"
import { formattedEventDates } from "../utils/event"
import { useTrackUserActivity } from "../utils/tracking"
import { useCommunityCustomization } from "../utils/useCommunityCustomization"
import {
  getCartMembershipProduct,
  useRequestCheckout,
  useSyncRequestCartMembership,
} from "../utils/useRequestCheckout"
import { IconType } from "./buttons/ModifyCounterIcon"
import { ModifyCounterIconSm } from "./buttons/ModifyCounterIconSm"
import { CartIcon } from "./icons/CartIcon"
import { ModalBase } from "./ModalBase"
import { TicketLimitAlertModal } from "./TicketLimitAlertModal"
import UbiqButton, { UbiqButtonSize } from "./UbiqButton"

export interface Cart {
  [code: string]: {
    [id: string]: {
      quantity: number
      product: BaseProductFragment
      fairPrice?: number
      variant?: { size: string; color: string; printfulVariantId?: number }
      deliveryMethod?: DeliveryMethod
    }
  }
}

type CartVariant = "default" | "blob"

const formatTicketValidity = (
  validFrom: Date | null | undefined,
  validTo: Date | null | undefined,
) => {
  if (!validFrom || !validTo) {
    return formattedEventDates(validFrom, validTo, DATE_FORMAT_DAY_MONTH_TIME)
  }

  return `${moment(validFrom).format(DATE_FORMAT_DAY_MONTH_TIME)} - ${moment(validTo).format("HH:mm")}`
}

export const Cart: FC<{
  onClick?: () => void
  className: string
  color: string
  variant?: CartVariant
}> = ({ onClick, className, color, variant = "default" }) => {
  if (variant === "blob") {
    return <BlobCart className={className} color={color} onClick={onClick} />
  }
  return <DefaultCart className={className} color={color} onClick={onClick} />
}

/**
 * Original cart design with Menu component and circular icon
 * Used on regular community pages
 */
const DefaultCart: FC<{ onClick?: () => void; className: string; color: string }> = ({
  onClick,
  className,
  color,
}) => {
  const { cart } = useContext(AppContext)
  const customizationData = useCommunityCustomization()
  const _track = useTrackUserActivity()
  const router = useRouter()
  const [showLimitAlert, setShowLimitAlert] = useState(false)

  if (!customizationData) return null
  const { code: communityCode } = customizationData

  const productCount = productInCartCount(cart[communityCode])

  const onCheckoutPage = router.pathname.includes("checkout")

  return (
    <>
      <Menu>
        {({ close }) => (
          <div className={className}>
            <div className="relative sm:h-auto h-[23px] text-left">
              <div className="relative" onClick={onClick && onClick}>
                <Menu.Button className="cartButton flex justify-center items-center sm:mt-0 mt-1 sm:scale-x-[1] scale-x-[-1] sm:bg-[#FFFFFF] sm:border-[1px] sm:border-black sm:rounded-full sm:w-[48px] sm:h-[48px]">
                  <CartIcon fill={color} />
                </Menu.Button>
                {productCount > 0 ? (
                  <div className="text-[#FFFFFF] font-sans text-[10px] absolute sm:top-[-2.5px] top-[-3px] right-[-2.5px] bg-[#FF1C5E] sm:border-[1px] sm:border-black rounded-full sm:w-[18px] sm:h-[18px] w-[5px] h-[5px] flex justify-center items-center">
                    <p
                      className="sm:block hidden"
                      style={{
                        textShadow:
                          "-0.5px -0.5px  1 black,  0.5px -0.5px  1 black, -0.5px  0.5px  1 black,  0.5px  0.5px  1 black",
                      }}
                    >
                      {productCount}
                    </p>
                  </div>
                ) : null}
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Menu.Items>
                  <div
                    className={`absolute sm:w-[388px] w-[97vw] ${
                      onCheckoutPage ? "right-[-12vw]" : "right-[-20vw]"
                    } sm:right-[2px] top-[36px] sm:top-14 mt-2 origin-top-right divide-y divide-gray-100 bg-[#fefef9] shadow-lg border border-[#232734] focus:outline-none`}
                    style={{ boxShadow: "2px 2px 0px rgba(0, 0, 0, 0.48)" }}
                  >
                    <CartContent
                      closeMenu={close}
                      onShowLimitAlert={() => setShowLimitAlert(true)}
                    />
                  </div>
                </Menu.Items>
              </Transition>
            </div>
          </div>
        )}
      </Menu>
      <TicketLimitAlertModal isOpen={showLimitAlert} onClose={() => setShowLimitAlert(false)} />
    </>
  )
}

/**
 * Blob-style cart for FleaMarket
 * Uses Popover with organic blob SVG icon
 */
const BlobCart: FC<{ onClick?: () => void; className: string; color: string }> = ({
  onClick,
  className,
  color,
}) => {
  const { cart, isCartOpen, openCart, closeCart } = useContext(AppContext)
  const customizationData = useCommunityCustomization()
  const _track = useTrackUserActivity()
  const router = useRouter()
  const panelRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)
  const [showLimitAlert, setShowLimitAlert] = useState(false)

  // Prevent hydration mismatch - Popover uses Portal which doesn't exist on server
  useEffect(() => {
    setMounted(true)
  }, [])

  // Close cart when clicking outside (only when mounted)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        closeCart()
      }
    }

    if (isCartOpen) {
      // Delay adding listener to avoid immediate close from the same click
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside)
      }, 0)
      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }
  }, [isCartOpen, closeCart])

  if (!customizationData) return null
  const { code: communityCode } = customizationData

  const productCount = productInCartCount(cart[communityCode])

  const onCheckoutPage = router.pathname.includes("checkout")

  const handleCartClick = () => {
    onClick?.()
    if (isCartOpen) {
      closeCart()
    } else {
      openCart()
    }
  }

  // Cart button SVG - shared between server and client render
  const CartButtonSvg = (
    <svg
      className="w-full h-full"
      fill="none"
      viewBox="0 0 49 49"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Organic blob background */}
      <path
        d="M24.0391 1.5C32.1108 1.5 38.3854 5.57306 42.6172 11.8438C45.1972 15.7276 47.4998 20.2161 47.5 25.0225C47.5 33.6976 43.2527 40.338 36.2295 44.4346H36.2285C32.7313 46.4899 28.2539 48.5 23.9951 48.5C17.4721 48.4999 11.8796 46.656 7.79004 42.8301C3.82929 39.1247 1.22224 33.508 0.588867 25.749L0.53418 24.9912C0.192606 19.4552 2.45546 14.3383 5.74219 10.2715L5.74316 10.2725C6.6849 9.11366 8.06411 8.02129 9.58105 7.02148C10.7158 6.27357 11.911 5.58754 13.0312 4.97168L14.1211 4.37988L14.127 4.37695C17.4706 2.51682 19.9788 1.50008 24.0391 1.5Z"
        fill="#FEFEF9"
        stroke="#1B1D22"
      />
      {/* Left wheel */}
      <path
        d="M20.6427 32.4441C20.6427 32.6568 20.5719 32.8367 20.4867 33.0152C20.4015 33.1937 20.2715 33.335 20.1156 33.4692C19.9596 33.6034 19.8426 33.8061 19.6462 33.8761C19.4498 33.946 19.2303 33.9189 19.0209 33.9189C18.8115 33.9189 18.5935 33.9346 18.4101 33.8661C18.218 33.7947 18.0563 33.6477 17.909 33.5149C17.7617 33.3821 17.6259 33.2194 17.5436 33.0352C17.4613 32.851 17.3848 32.654 17.3848 32.4441C17.3848 32.2342 17.5176 32.0472 17.5956 31.8645C17.6736 31.6817 17.7978 31.529 17.9436 31.3876C18.0895 31.2463 18.2267 31.0949 18.4187 31.0178C18.6108 30.9408 18.8087 30.8594 19.0195 30.8594C19.2303 30.8594 19.4282 30.9693 19.6202 31.045C19.8123 31.1206 19.9972 31.2177 20.1502 31.3562C20.3033 31.4947 20.4246 31.6646 20.5113 31.8516C20.5979 32.0386 20.6398 32.2328 20.6398 32.4455L20.6427 32.4441Z"
        fill={color}
      />
      {/* Right wheel */}
      <path
        d="M28.9289 32.4442C28.9289 32.6569 28.9564 32.8796 28.8697 33.0581C28.7831 33.2366 28.6011 33.3793 28.4451 33.5135C28.2892 33.6477 28.1159 33.7605 27.9209 33.8305C27.726 33.9004 27.5224 33.9061 27.313 33.9061C27.1036 33.9061 26.9202 33.8462 26.7368 33.7777C26.5447 33.7063 26.3945 33.5992 26.2487 33.465C26.1028 33.3308 25.8847 33.2337 25.8024 33.0495C25.7201 32.8654 25.7432 32.6526 25.7432 32.4428C25.7432 32.2329 25.7346 32.013 25.8125 31.8317C25.8905 31.6504 26.0523 31.4876 26.1981 31.3463C26.344 31.205 26.5129 31.0765 26.705 30.9979C26.8971 30.9194 27.1021 30.8438 27.313 30.8438C27.5238 30.8438 27.7202 30.9737 27.9108 31.0493C28.1014 31.125 28.2646 31.2435 28.4163 31.3834C28.5679 31.5233 28.7325 31.6561 28.8192 31.8431C28.9058 32.0302 28.9275 32.2315 28.9275 32.4442H28.9289Z"
        fill={color}
      />
      {/* Cart body */}
      <path
        d="M34.498 16.4993C33.906 16.5492 34.2672 16.503 33.4195 16.8499C32.8519 17.0798 32.7133 17.1569 32.7133 17.1569C32.2757 17.3896 31.6057 17.7951 31.2057 18.2419C31.0237 18.4447 30.6656 18.5318 30.4143 18.666C30.0923 18.8373 29.0568 19.13 28.7088 19.1285C28.0503 19.1357 23.7266 19.1414 23.0999 19.1043C21.9403 19.0786 19.9416 19.2856 18.8051 19.1085C17.8592 19.267 17.9849 18.9372 17.0736 19.2213C16.2693 19.2156 16.3458 19.0643 15.5833 19.1657C15.3162 19.2013 14.2648 19.2613 13.8403 19.584C13.4576 19.8709 13.6453 20.7304 13.8027 21.4271C13.8619 21.6927 13.9356 21.9568 14.0222 22.2223C14.4843 23.7171 14.9566 25.7402 15.3277 26.5668C15.7422 27.5334 15.3234 26.6139 15.5862 27.1993C15.8721 27.8218 16.6462 28.0131 16.9957 28.0545C18.2434 28.4271 18.0802 28.0045 19.2485 28.1201C19.4954 28.1458 19.7395 28.1558 19.9763 28.123C20.7084 28.0202 21.4507 28.2472 22.1757 28.1301C23.1663 28.1687 24.1223 28.1044 25.1058 28.1573C25.8134 28.0045 26.5166 28.0074 27.1925 28.0973C27.4524 28.1316 27.7239 28.1173 28.0012 28.0773C28.1124 28.0616 28.9066 28.073 29.0193 28.0673C29.5897 28.0373 29.207 29.1624 28.7695 29.5236C28.7666 29.525 28.7651 29.5278 28.7622 29.5293C28.2655 29.9319 27.4351 29.6835 27.1882 29.732C26.4849 29.8762 26.5961 29.6906 25.8841 29.7006C25.0133 30.0718 18.0686 29.7748 17.205 29.9847C16.8339 29.9076 16.0498 29.9619 16.148 30.7785C16.1768 31.0198 16.0628 31.2554 16.301 31.2582C16.3054 31.2582 16.3111 31.2582 16.3155 31.2582C17.1863 31.6294 18.0181 31.2425 18.8802 31.4524C19.8535 31.2525 21.6327 31.5266 22.6378 31.4966C23.3627 31.4481 24.1295 31.4338 24.8992 31.4252L27.6546 31.3838C29.1651 31.3239 29.633 31.3125 30.3175 30.7799C30.9241 30.3274 31.597 29.0653 31.8266 28.1887C31.857 26.9566 32.0028 26.247 31.9624 25.132C32.0057 24.9806 32.049 24.8107 32.0923 24.6052C32.4721 23.4644 32.1905 23.2132 32.4837 22.071C32.5501 21.9225 32.6238 21.3443 32.6281 21.2144C32.6382 20.8917 32.6281 20.4035 32.7725 20.0722C32.9386 19.6868 33.4469 19.4855 33.5494 19.3798C33.7213 19.1856 34.1054 18.7916 34.7379 18.5717C35.3459 18.3576 35.748 17.9995 35.748 17.1571C35.248 16.3063 34.498 16.4993 34.498 16.4993Z"
        fill={color}
      />
    </svg>
  )

  // SSR placeholder - render just the button without Popover to avoid hydration mismatch
  if (!mounted) {
    return (
      <div className={className}>
        <div className="relative text-left">
          <div className="relative">
            <button
              className="cartButton relative w-[40px] h-[40px] lg:w-[48px] lg:h-[48px]"
              type="button"
            >
              {CartButtonSvg}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Popover>
      <div className={className}>
        <div className="relative text-left" ref={panelRef}>
          <div className="relative">
            <Popover.Button
              as="button"
              className="cartButton relative w-[40px] h-[40px] lg:w-[48px] lg:h-[48px]"
              onClick={handleCartClick}
            >
              {CartButtonSvg}
            </Popover.Button>
            {/* Counter badge - organic blob shape */}
            {productCount > 0 && (
              <svg
                className="absolute w-[15px] h-[15px] lg:w-[18px] lg:h-[18px] top-[-2px] right-[-2px] lg:top-[-3px] lg:right-[-3px] pointer-events-none"
                fill="none"
                viewBox="0 0 18 18"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Pink blob background */}
                <path
                  d="M9.01465 0.5C11.7392 0.5 13.8985 1.78367 15.4258 3.82324L15.7227 4.24121C16.6814 5.68483 17.5 7.30283 17.5 9.00879C17.4999 12.1431 15.9729 14.5335 13.4287 16.0176L13.4268 16.0186C12.1277 16.7819 10.509 17.5 8.998 17.5C6.61044 17.4999 4.59683 16.8258 3.13477 15.458C1.67369 14.0911 0.703555 11.9758 0.511719 8.97656C0.390095 7.00033 1.19702 5.15588 2.39551 3.67285L2.39648 3.67383C2.72003 3.27587 3.20612 2.88669 3.76465 2.51855C4.31909 2.15314 4.91587 1.82712 5.44336 1.54297L5.44922 1.53906C6.67716 0.855922 7.56458 0.500028 9.01465 0.5Z"
                  fill="#FF1C5E"
                  stroke="#252934"
                />
                {/* Counter text */}
                <text
                  dominantBaseline="central"
                  fill="#FEFEF9"
                  fontFamily="sans-serif"
                  fontSize="9"
                  fontWeight="bold"
                  textAnchor="middle"
                  x="9"
                  y="9.5"
                >
                  {productCount > 9 ? "9+" : productCount}
                </text>
              </svg>
            )}
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100 "
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
            show={isCartOpen}
          >
            <Popover.Panel
              className="absolute w-[388px] max-w-[calc(100vw-32px)] right-0 top-[60px] origin-top-right divide-y divide-gray-100 bg-[#fefef9] shadow-lg border border-[#232734] focus:outline-none"
              static
              style={{ boxShadow: "2px 2px 0px rgba(0, 0, 0, 0.48)" }}
            >
              <CartContent closeMenu={closeCart} onShowLimitAlert={() => setShowLimitAlert(true)} />
            </Popover.Panel>
          </Transition>
          <TicketLimitAlertModal isOpen={showLimitAlert} onClose={() => setShowLimitAlert(false)} />
        </div>
      </div>
    </Popover>
  )
}

const CartContent: FC<{
  closeMenu?: () => void
  onShowLimitAlert?: () => void
}> = ({ closeMenu, onShowLimitAlert }) => {
  const router = useRouter()
  const customizationData = useCommunityCustomization()
  const { cart, adjustCart } = useContext(AppContext)
  const track = useTrackUserActivity()
  const { communityLoaded } = useRequestCheckout()
  const membershipProduct = getCartMembershipProduct(cart, customizationData?.code)

  useSyncRequestCartMembership({
    adjustCart,
    cart,
    code: customizationData?.code,
    communityLoaded,
    membershipProduct,
  })

  // With items in the cart, checkout is the likely next step — warm up its page
  // bundle and the Stripe.js script so the transition doesn't pay for them at click time.
  const cartCode = customizationData?.code
  const cartUrl = customizationData?.url
  const hasCartItems = cartCode ? productInCartCount(cart[cartCode]) > 0 : false
  useEffect(() => {
    if (!cartUrl || !hasCartItems) return
    void router.prefetch(`${cartUrl}/checkout`).catch(() => {})
    warmupStripeJs()
  }, [cartUrl, hasCartItems, router])

  if (!customizationData) return null
  const { code: communityCode, url } = customizationData

  const subTotalValue = calculateSubtotalPrice(cart[communityCode])
  const totalValue = subTotalValue

  // Detect mixed currencies in cart
  const cartItems = Object.values(cart[communityCode] ?? {})
  const currencies = new Set(cartItems.map((item) => item.product.currency))
  const hasMultipleCurrencies = currencies.size > 1
  const cartCurrency = currencies.size === 1 ? Array.from(currencies)[0] : null

  const onCheckoutPage = router.pathname.includes("checkout")

  const isButtonClickable = (product: BaseProductFragment, quantity: number) =>
    product.maxAllowedPurchases >= 0 &&
    product.limit >= 0 &&
    quantity >= product.maxAllowedPurchases

  return (
    <>
      <div className="relative">
        <div
          className={`h-[16px] w-[16px] border-t-[1px] border-l-[1px] border-black rotate-[45deg] bg-[#FFFFFF] absolute sm:right-[13px] ${
            onCheckoutPage ? "right-[calc(5.1vw+27px)]" : "right-[calc(13vw+27px)]"
          } sm:top-[-9px] top-[-8.5px]`}
        />
        <div className="pt-4 px-4">
          <div className="flex justify-start sm:justify-between text-sm leading-[18px] text-[#232734] mb-1">
            <p className="font-bold text-[14px] leading-6 text-left sm:text-sm sm:leading-[14px]">
              Cart summary
            </p>
          </div>
          {Object.values(cart[communityCode] ?? []).map(
            ({ product, quantity, fairPrice, variant }) => {
              if (quantity > 0) {
                // Membership lines only ever exist via the pairing auto-add (both
                // request and normal bundle checkout) — always locked, qty 1.
                const isLockedMembership = product.productType === ProductType.Membership
                const displayPrice = isLockedMembership
                  ? `${divideNumberByGrade(fairPrice ?? product.price)} ${product.currency} / year`
                  : fairPrice === undefined
                    ? product.displayPrice
                    : `${divideNumberByGrade(fairPrice)} ${product.currency}`
                return (
                  <div
                    className={`border border-[#232734] p-1 pb-4 sm:p-2 mt-2 mb-2 sm:h-auto ${
                      isLockedMembership ? "h-auto" : "h-[59px]"
                    }`}
                    key={product.id}
                  >
                    <div className="flex justify-between sm:pt-0 pt-[2px] items-center flex-row">
                      <div className="flex gap-2">
                        <img
                          alt=""
                          className="w-[45px] h-[45px] object-contain"
                          src={product.mediaUrl || ""}
                        />
                        <div>
                          <p className="text-xs leading-4 font-bold mb-1">{product.name}</p>
                          {variant && (variant.size || variant.color) ? (
                            <p className="text-[10px] leading-3 text-gray-600 mb-1">
                              {formatVariantDisplay(variant)}
                            </p>
                          ) : product.productType === ProductType.Membership ? (
                            product.shortTerms ? (
                              <p className="max-w-[190px] text-[10px] leading-3 text-gray-600">
                                {product.shortTerms}
                              </p>
                            ) : null
                          ) : product.productType === ProductType.Ticket ? (
                            <p className="text-[10px] leading-3">
                              Valid {formatTicketValidity(product.validFrom, product.validTo)}
                            </p>
                          ) : product.productType !== ProductType.Merch ? (
                            <p className="text-[10px] leading-3">
                              Valid{" "}
                              {formattedEventDates(
                                product.startDate,
                                product.endDate,
                                DATE_FORMAT_DAY_MONTH,
                              )}
                            </p>
                          ) : product.description ? (
                            <p className="text-[10px] leading-3 text-gray-600 mb-1 line-clamp-2">
                              {product.description}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 sm:flex-row-reverse flex-col justify-between items-center sm:block sm:w-fit sm:gap-0 gap-1">
                        <p className="whitespace-nowrap text-[13px] leading-5 font-bold text-right sm:mb-1">
                          {displayPrice}
                        </p>
                        {isLockedMembership ? (
                          <p className="text-[13px] text-right">1</p>
                        ) : (
                          <div className="flex gap-2 text-[13px] sm:pr-0 pr-5">
                            <button
                              className="group"
                              onClick={(e) => {
                                e.stopPropagation()
                                adjustCart(communityCode, product, -1, variant)
                              }}
                              type="button"
                            >
                              <ModifyCounterIconSm type={IconType.MINUS} />
                            </button>
                            <p>{quantity}</p>

                            <button
                              className={`group ${
                                isButtonClickable(product, quantity) ? "opacity-30" : ""
                              }`}
                              onClick={(e) => {
                                e.stopPropagation()
                                if (isButtonClickable(product, quantity)) {
                                  onShowLimitAlert?.()
                                  return
                                }
                                adjustCart(communityCode, product, 1, variant)
                              }}
                              type="button"
                            >
                              <ModifyCounterIconSm type={IconType.PLUS} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }
            },
          )}
        </div>
        {productInCartCount(cart[communityCode]) > 0 ? (
          <div className="flex flex-col px-4 pb-6">
            {!hasMultipleCurrencies && (
              <div className="flex justify-between items-center px-2 py-1 text-[#232734] border border-[#232734] text-[13px] leading-5 uppercase font-bold mb-2 sm:h-auto h-[36px]">
                <p>Total</p>
                <p>
                  {totalValue.toFixed(2)} {cartCurrency}
                </p>
              </div>
            )}
            <UbiqButton
              onClick={() => {
                // The click only; checkout entry is tracked once, by the checkout page itself.
                track({
                  data: {
                    currency: cartCurrency,
                    items: checkoutItems(cart[communityCode]),
                    totalAmount: totalValue,
                  },
                  type: UserActivityEventType.CartCheckoutClicked,
                })
                void router.push(`${url}/checkout`)
                closeMenu?.()
              }}
              size={UbiqButtonSize.MEDIUM}
              styles="uppercase"
              title="checkout"
            />
          </div>
        ) : (
          <div className="flex items-center flex-col gap-6 text-center text-xs text-[#232734] my-6">
            <p className="w-[202px]">
              There is nothing here.
              <br /> Add a product from the Shop.
            </p>
            <UbiqButton onClick={closeMenu} styles="uppercase" title="got it" />
          </div>
        )}
      </div>
    </>
  )
}

const _CartModal: FC<{
  onClose: () => void
  show: boolean
}> = ({ onClose, show }) => {
  return (
    <ModalBase
      addBgOpacity
      dialogPanelStyles="px-6 py-16 max-w-[375px] w-full"
      onClose={onClose}
      show={show}
    >
      <CartContent closeMenu={onClose} />
    </ModalBase>
  )
}
